import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Alert, Typography, MenuItem, TextField, Switch, FormControlLabel,
  Paper, Chip, CircularProgress, Link,
} from '@mui/material';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SaveIcon from '@mui/icons-material/Save';
import { superAdminAPI } from '../../services/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { apiErrorMessage } from '../../components/superadmin/SuperAdminDataTable';
import { RADIUS, brandColors, brandCss } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';

interface TenantReportRow {
  reportKey: string;
  name: string;
  description?: string;
  moduleKey: string;
  chartType: string;
  enabled: boolean;
}

export const TenantReportAccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tenants, setTenants] = useState<any[]>([]);
  const [tenantId, setTenantId] = useState(searchParams.get('tenant') || '');
  const [companyName, setCompanyName] = useState('');
  const [reports, setReports] = useState<TenantReportRow[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loadingReports, setLoadingReports] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    superAdminAPI.getTenants()
      .then((r) => setTenants(r.data.filter((t: any) => t.status === 'active')))
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load tenants')))
      .finally(() => setLoadingTenants(false));
  }, []);

  const loadReports = async (id: string) => {
    if (!id) return;
    setLoadingReports(true);
    setError('');
    try {
      const res = await superAdminAPI.getTenantReportAccess(id);
      setCompanyName(res.data.companyName);
      setReports(res.data.reports || []);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load report access'));
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    if (tenantId) loadReports(tenantId);
  }, [tenantId]);

  const grouped = useMemo(() => {
    const map: Record<string, TenantReportRow[]> = {};
    reports.forEach((r) => {
      const g = r.moduleKey || 'Other';
      if (!map[g]) map[g] = [];
      map[g].push(r);
    });
    return map;
  }, [reports]);

  const toggleReport = (reportKey: string, enabled: boolean) => {
    setReports((prev) => prev.map((r) =>
      r.reportKey === reportKey ? { ...r, enabled } : r
    ));
  };

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setError('');
    try {
      const res = await superAdminAPI.updateTenantReportAccess(
        tenantId,
        reports.map((r) => ({ reportKey: r.reportKey, enabled: r.enabled }))
      );
      setReports(res.data.reports || []);
      setMessage(`Report access saved for ${res.data.companyName}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = reports.filter((r) => r.enabled).length;

  return (
    <Box>
      <PageHeader
        title="Tenant Report Access"
        subtitle={getPageSubtitle('tenant_report_access')}
        icon={<AssessmentIcon />}

        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!tenantId || saving || loadingReports}
            sx={catchyPrimaryButtonSx}
          >
            {saving ? 'Saving…' : 'Save Access'}
          </Button>
        }
      />

      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select
          label="Tenant"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          disabled={loadingTenants}
          sx={{ ...catchyTextFieldSx, minWidth: 280 }}
          size="small"
        >
          <MenuItem value="">— Choose tenant —</MenuItem>
          {tenants.map((t) => (
            <MenuItem key={t._id} value={t._id}>{t.companyName}</MenuItem>
          ))}
        </TextField>

        {tenantId && !loadingReports && (
          <>
            <Chip label={companyName} color="primary" variant="outlined" size="small" />
            <Chip label={`${enabledCount} / ${reports.length} on`} size="small" sx={{ bgcolor: brandCss.green100 }} />
          </>
        )}
      </Box>

      {!tenantId && (
        <Typography color="text.secondary" fontSize={14}>
          Select a tenant above to manage which reports their owner and staff can run.
        </Typography>
      )}

      {tenantId && loadingReports && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="success" />
        </Box>
      )}

      {tenantId && !loadingReports && reports.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No report templates found.{' '}
          <Link component={RouterLink} to="/super-admin/report-templates" fontWeight={700}>
            Create report templates
          </Link>{' '}
          first, then return here to assign them to tenants.
        </Alert>
      )}

      {tenantId && !loadingReports && reports.length > 0 && Object.entries(grouped).map(([group, items]) => (
        <Paper
          key={group}
          sx={{ p: 2.5, mb: 2, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}
        >
          <Typography fontWeight={800} fontSize={13} color="#166534" mb={1.5} letterSpacing={0.5}>
            {group.toUpperCase()}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
            {items.map((report) => (
              <Box
                key={report.reportKey}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 1,
                  borderRadius: `${RADIUS}px`,
                  bgcolor: report.enabled ? '#f0fdf4' : '#f8fafc',
                  border: `1px solid ${report.enabled ? '#bbf7d0' : brandColors.border}`,
                }}
              >
                <Box sx={{ pr: 1 }}>
                  <Typography fontSize={13} fontWeight={600}>{report.name}</Typography>
                  <Typography fontSize={11} color="text.secondary">
                    {report.reportKey} · {report.chartType}
                  </Typography>
                  {report.description && (
                    <Typography fontSize={10} color="text.secondary">{report.description}</Typography>
                  )}
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={report.enabled}
                      onChange={(e) => toggleReport(report.reportKey, e.target.checked)}
                      color="success"
                      size="small"
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
            ))}
          </Box>
        </Paper>
      ))}

      {tenantId && !loadingReports && reports.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate('/super-admin/report-templates')} sx={{ borderRadius: 2 }}>
            Manage Report Templates
          </Button>
        </Box>
      )}
    </Box>
  );
};
